/**
 * Service de vérification de connectivité des sites web
 */

export interface ConnectivityResult {
  isAccessible: boolean;
  statusCode?: number;
  error?: string;
  responseTime?: number;
  finalUrl?: string; // URL finale après redirections
}

export const connectivityService = {
  /**
   * Normalise une URL en ajoutant https:// si nécessaire
   */
  normalizeUrl(url: string): string {
    if (!url || url.trim() === '') {
      return '';
    }

    const trimmedUrl = url.trim();
    
    // Si l'URL commence déjà par http:// ou https://, la retourner telle quelle
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }
    
    // Sinon, ajouter https://
    return `https://${trimmedUrl}`;
  },

  /**
   * Vérifie si une URL est accessible (retourne un status 200-299)
   */
  async checkConnectivity(url: string): Promise<ConnectivityResult> {
    const startTime = Date.now();
    
    try {
      // Normaliser l'URL
      const normalizedUrl = this.normalizeUrl(url);
      
      if (!normalizedUrl) {
        return {
          isAccessible: false,
          error: 'URL vide ou invalide'
        };
      }

      // Valider le format de l'URL
      try {
        new URL(normalizedUrl);
      } catch (urlError) {
        return {
          isAccessible: false,
          error: 'Format d\'URL invalide'
        };
      }

      // Faire une requête HEAD pour vérifier la connectivité
      // On utilise fetch avec mode 'no-cors' pour éviter les problèmes CORS
      const response = await fetch(normalizedUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        redirect: 'follow'
      });

      const responseTime = Date.now() - startTime;

      // En mode no-cors, response.ok sera toujours false
      // On considère que si la requête n'a pas levé d'exception, le site est accessible
      return {
        isAccessible: true,
        statusCode: response.status || 200,
        responseTime,
        finalUrl: normalizedUrl
      };

    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      return {
        isAccessible: false,
        error: error instanceof Error ? error.message : 'Erreur de connectivité',
        responseTime
      };
    }
  },

  /**
   * Vérifie la connectivité avec une méthode alternative (via un proxy ou service)
   * Cette méthode peut être utilisée si la vérification directe échoue à cause de CORS
   */
  async checkConnectivityViaProxy(url: string): Promise<ConnectivityResult> {
    const startTime = Date.now();
    
    try {
      const normalizedUrl = this.normalizeUrl(url);
      
      if (!normalizedUrl) {
        return {
          isAccessible: false,
          error: 'URL vide ou invalide'
        };
      }

      // Utiliser un service de proxy public pour vérifier la connectivité
      // Alternative : créer une fonction Supabase Edge pour faire la vérification
      const proxyUrl = `https://api.allorigins.win/head?url=${encodeURIComponent(normalizedUrl)}`;
      
      const response = await fetch(proxyUrl);
      const data = await response.json();
      
      const responseTime = Date.now() - startTime;
      
      if (data.status && data.status.http_code) {
        const statusCode = data.status.http_code;
        const isAccessible = statusCode >= 200 && statusCode < 400;
        
        return {
          isAccessible,
          statusCode,
          responseTime,
          finalUrl: normalizedUrl
        };
      }
      
      return {
        isAccessible: false,
        error: 'Impossible de vérifier la connectivité',
        responseTime
      };

    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      return {
        isAccessible: false,
        error: error instanceof Error ? error.message : 'Erreur de connectivité',
        responseTime
      };
    }
  },

  /**
   * Vérifie la connectivité avec fallback automatique
   */
  async checkConnectivityWithFallback(url: string): Promise<ConnectivityResult> {
    // Essayer d'abord la méthode directe
    const directResult = await this.checkConnectivity(url);
    
    // Si la méthode directe échoue, essayer via proxy
    if (!directResult.isAccessible && directResult.error?.includes('CORS')) {
      return await this.checkConnectivityViaProxy(url);
    }
    
    return directResult;
  }
};
